"use client";

import { cn } from "@/lib/utils";
import { pushGtmEvent } from "@/utils/utils";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { LEAD_DROP_VARIANTS } from "@/constants/leadConstants";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

interface MenuItem {
  id: number;
  title: string;
  slug: string;
  url?: string;
  priority: number;
  status: boolean;
  icon?: string;
  icon_alt?: string;
  text_color?: string;
  children?: MenuItem[];
}

interface RouteItem {
  id: number;
  route: string;
  label: string;
  textColor?: string;
  nestedRoutes?: {
    id: number;
    label: string;
    route: string;
    textColor?: string;
    icon?: string;
    iconAlt?: string;
  }[];
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

/** MySQL TINYINT(1) → boolean; Sequelize may return 0/1, "0"/"1", or boolean. */
function toBool(value: unknown, whenMissing: boolean): boolean {
  if (value == null) return whenMissing;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const t = value.trim().toLowerCase();
    if (["0", "false", "no", "off", ""].includes(t)) return false;
    if (["1", "true", "yes", "on"].includes(t)) return true;
  }
  return Boolean(value);
}

const isExternal = (href: string) =>
  /^https?:\/\//i.test(href) ||
  href.startsWith("//") ||
  href.startsWith("tel:") ||
  href.startsWith("mailto:");

/** Phone CTA: pass through real URLs; otherwise turn a bare number into `tel:`. */
const buildTelHref = (raw?: string) => {
  if (!raw) return undefined;
  const v = raw.trim();
  if (/^(https?:\/\/|\/\/|mailto:|tel:)/i.test(v)) return v;
  const digits = v.replace(/[^\d+]/g, "");
  return digits ? `tel:${digits}` : undefined;
};

/** WhatsApp CTA: pass through real URLs; otherwise turn a bare number into wa.me. */
const buildWhatsAppHref = (raw?: string) => {
  if (!raw) return undefined;
  const v = raw.trim();
  if (/^(https?:\/\/|whatsapp:|\/\/)/i.test(v)) return v;
  const digits = v.replace(/\D/g, "");
  return digits ? `https://wa.me/${digits}` : undefined;
};

/* ------------------------------------------------------------------ */
/* Default inline-SVG icons (used when no CMS icon uploaded)           */
/* ------------------------------------------------------------------ */

const PhoneIcon = ({ color, className }: { color: string; className?: string }) => (
  <svg className={className ?? "size-full"} viewBox="0 0 14 14" fill="none" aria-hidden xmlns="http://www.w3.org/2000/svg">
    <path d="M6.44616 8.72092L5.36699 9.80008C5.13949 10.0276 4.77783 10.0276 4.54449 9.80591C4.48033 9.74175 4.41616 9.68342 4.35199 9.61925C3.75116 9.01258 3.20866 8.37675 2.72449 7.71175C2.24616 7.04675 1.86116 6.38175 1.58116 5.72258C1.30699 5.05758 1.16699 4.42175 1.16699 3.81508C1.16699 3.41841 1.23699 3.03925 1.37699 2.68925C1.51699 2.33341 1.73866 2.00675 2.04783 1.71508C2.42116 1.34758 2.82949 1.16675 3.26116 1.16675C3.42449 1.16675 3.58783 1.20175 3.73366 1.27175C3.88533 1.34175 4.01949 1.44675 4.12449 1.59841L5.47783 3.50591C5.58283 3.65175 5.65866 3.78591 5.71116 3.91425C5.76366 4.03675 5.79283 4.15925 5.79283 4.27008C5.79283 4.41008 5.75199 4.55008 5.67033 4.68425C5.59449 4.81841 5.48366 4.95841 5.34366 5.09841L4.90033 5.55925C4.83616 5.62341 4.80699 5.69925 4.80699 5.79258C4.80699 5.83925 4.81283 5.88008 4.82449 5.92675C4.84199 5.97341 4.85949 6.00841 4.87116 6.04342C4.97616 6.23591 5.15699 6.48675 5.41366 6.79008C5.67616 7.09342 5.95616 7.40258 6.25949 7.71175C6.31783 7.77008 6.38199 7.82842 6.44033 7.88675C6.67366 8.11425 6.67949 8.48758 6.44616 8.72092Z" fill={color}/>
    <path d="M12.8154 10.6924C12.8154 10.8558 12.7863 11.0249 12.7279 11.1883C12.7104 11.2349 12.6929 11.2816 12.6696 11.3283C12.5704 11.5383 12.4421 11.7366 12.2729 11.9233C11.9871 12.2383 11.6721 12.4658 11.3163 12.6116C11.3104 12.6116 11.3046 12.6174 11.2988 12.6174C10.9546 12.7574 10.5813 12.8333 10.1788 12.8333C9.58378 12.8333 8.94794 12.6933 8.27711 12.4074C7.60628 12.1216 6.93544 11.7366 6.27044 11.2524C6.04294 11.0833 5.81544 10.9141 5.59961 10.7333L7.50711 8.82577C7.67044 8.94827 7.81628 9.04161 7.93878 9.10577C7.96794 9.11744 8.00294 9.13494 8.04378 9.15244C8.09044 9.16994 8.13711 9.17577 8.18961 9.17577C8.28878 9.17577 8.36461 9.14078 8.42878 9.07661L8.87211 8.63911C9.01794 8.49328 9.15794 8.38244 9.29211 8.31244C9.42628 8.23077 9.56044 8.18994 9.70628 8.18994C9.81711 8.18994 9.93378 8.21327 10.0621 8.26577C10.1904 8.31827 10.3246 8.39411 10.4704 8.49328L12.4013 9.86411C12.5529 9.96911 12.6579 10.0916 12.7221 10.2374C12.7804 10.3833 12.8154 10.5291 12.8154 10.6924Z" fill={color}/>
  </svg>
);

const WhatsAppIcon = ({ color, className }: { color: string; className?: string }) => (
  <svg className={className ?? "size-full"} viewBox="0 0 14 14" fill="none" aria-hidden xmlns="http://www.w3.org/2000/svg">
    <path d="M12.8217 6.65593C12.6233 3.27259 9.54917 0.6651 6.00834 1.24843C3.57 1.65093 1.61584 3.62842 1.23667 6.06675C1.01501 7.47842 1.30669 8.81426 1.94252 9.91676L1.42335 11.8476C1.30668 12.2851 1.70917 12.6818 2.14083 12.5593L4.04251 12.0343C4.90584 12.5418 5.915 12.8334 6.99417 12.8334C10.2842 12.8334 13.0142 9.93426 12.8217 6.65593ZM9.84668 9.17009C9.79418 9.27509 9.73 9.37426 9.64833 9.4676C9.5025 9.6251 9.34499 9.74176 9.16999 9.81176C8.99499 9.8876 8.80251 9.9226 8.59834 9.9226C8.30084 9.9226 7.98 9.8526 7.6475 9.70677C7.30917 9.56093 6.97669 9.36842 6.64419 9.12926C6.30585 8.88425 5.99084 8.61009 5.68751 8.31259C5.38418 8.00926 5.11583 7.68842 4.87083 7.35592C4.63166 7.02342 4.43917 6.69092 4.29917 6.35842C4.15917 6.02592 4.08918 5.7051 4.08918 5.40176C4.08918 5.20343 4.12418 5.01093 4.19418 4.83593C4.26418 4.6551 4.37502 4.49177 4.53252 4.34593C4.71919 4.15927 4.92334 4.07176 5.13918 4.07176C5.22084 4.07176 5.30249 4.08926 5.37833 4.12426C5.45416 4.15926 5.52418 4.21176 5.57668 4.2876L6.25333 5.24425C6.30583 5.32009 6.34667 5.38426 6.37 5.44842C6.39917 5.51259 6.41084 5.57092 6.41084 5.62925C6.41084 5.69925 6.38751 5.76926 6.34668 5.83926C6.30585 5.90926 6.25333 5.97926 6.18333 6.04926L5.96166 6.28259C5.92666 6.31759 5.91502 6.35259 5.91502 6.39926C5.91502 6.42259 5.92083 6.44592 5.92666 6.46926C5.93833 6.49259 5.94418 6.51009 5.95002 6.52759C6.00252 6.62676 6.09583 6.74925 6.22417 6.90092C6.35833 7.05258 6.49836 7.21009 6.65002 7.36176C6.80752 7.51926 6.95918 7.65925 7.11668 7.79342C7.26835 7.92175 7.39666 8.00926 7.49583 8.06176C7.51333 8.0676 7.53085 8.07925 7.54835 8.08508C7.57168 8.09675 7.59502 8.09676 7.62418 8.09676C7.67668 8.09676 7.71169 8.07926 7.74669 8.04426L7.96833 7.82259C8.04416 7.74676 8.11419 7.69426 8.17836 7.65926C8.24836 7.61843 8.31251 7.59509 8.38835 7.59509C8.44668 7.59509 8.50501 7.60676 8.56918 7.63592C8.63335 7.66509 8.70333 7.7001 8.77333 7.7526L9.74168 8.44093C9.81752 8.49343 9.87 8.5576 9.905 8.6276C9.93417 8.70343 9.95168 8.77342 9.95168 8.85509C9.91668 8.95425 9.89335 9.06509 9.84668 9.17009Z" fill={color}/>
  </svg>
);

/** Hamburger — 3 lines (Figma 12366:17512). */
const HamburgerIcon = ({ color, className }: { color: string; className?: string }) => (
  <svg className={className ?? "size-full"} viewBox="0 0 22 22" fill="none" aria-hidden xmlns="http://www.w3.org/2000/svg">
    <path d="M1.375 2.75H20.625" stroke={color} strokeWidth="1.65" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M1.375 11H20.625" stroke={color} strokeWidth="1.65" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M1.375 19.25H20.625" stroke={color} strokeWidth="1.65" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CloseIcon = ({ color, className }: { color: string; className?: string }) => (
  <svg className={className ?? "size-full"} viewBox="0 0 24 24" fill="none" aria-hidden xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6 6 18M6 6l12 12" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronIcon = ({ open }: { open: boolean }) => (
  <svg width="12" height="8" viewBox="0 0 10 6" fill="none" className={cn("transition-transform duration-200", open && "rotate-180")} aria-hidden>
    <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DropdownItemIcon = ({ color, size = 20 }: { color: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M14.5 9.5 7 17a3.54 3.54 0 0 1-5-5l7.5-7.5a2.36 2.36 0 0 1 3.34 3.34L7 13.69" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * Apply Now trailing glyph — Figma `vuesax/linear/send` (node 13512:16896).
 *
 * The exported asset draws the arrow pointing up-LEFT and the design composes
 * `scaleY(-1) rotate(180deg)` on top of it; that composition reduces exactly to
 * `scaleX(-1)`, so the mirror is applied here as a single transform rather than
 * re-drawing the path.
 */
const SendIcon = ({ color, className }: { color: string; className?: string }) => (
  <svg className={cn("-scale-x-100", className ?? "size-full")} viewBox="0 0 16 16" fill="none" aria-hidden xmlns="http://www.w3.org/2000/svg">
    <path d="M3.33333 3.33333L12.6667 12.6667" stroke={color} strokeWidth="2.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3.33333 10.18V3.33333H10.18" stroke={color} strokeWidth="2.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * PRD cap: a navbar renders at most 7 top-level items. Enforced here as well as
 * in the admin form so an over-long menu saved before the cap existed (or via a
 * direct API write) still renders on-design.
 */
const MAX_NAV_ITEMS = 7;

/**
 * Apply Now pill (Figma node 13512:16894 and its per-breakpoint siblings).
 *
 * Box is 7px-rounded with a 1px border matching the fill, a 10px gap between
 * label and trailing glyph, and Google Sans SemiBold at 1.49 line-height. The
 * border is kept even though it matches the fill so the pill's box height lines
 * up with the outlined phone pill beside it.
 *
 * Sizing steps with the breakpoint, mirroring the design frames:
 *   sm 36h/10px/12pt · md 36h/10px/14pt · lg 41h/14px/14pt · xl 44h/14px/16pt.
 *
 * Case is left to the CMS — the design types its labels in caps, so a stored
 * "Apply Now" renders as typed rather than being forced upper here.
 */
const ApplyNowCta = ({
  label,
  href,
  bgColor,
  textColor,
  icon,
  iconAlt,
  compact = false,
  onClick,
}: {
  label: string;
  href?: string;
  bgColor: string;
  textColor: string;
  icon?: string;
  iconAlt?: string;
  /** Mobile bar sizing (Figma 13539:17221): smaller pill, 10pt label, 12px glyph. */
  compact?: boolean;
  onClick: () => void;
}) => {
  const className = compact
    ? "flex shrink-0 items-center justify-center gap-[6px] whitespace-nowrap rounded-[6px] border border-solid px-[8px] py-[6px] font-google-sans text-[10px] font-semibold leading-[1.4] transition-opacity hover:opacity-90"
    : "flex h-[36px] shrink-0 items-center justify-center gap-[10px] whitespace-nowrap rounded-[7px] border border-solid px-[10px] font-google-sans text-[12px] font-semibold leading-[1.49] transition-opacity hover:opacity-90 md:text-[14px] lg:h-[41px] lg:px-[14px] xl:h-[44px] xl:text-[16px]";
  const style = {
    backgroundColor: bgColor,
    borderColor: bgColor,
    color: textColor,
  };

  const content = (
    <>
      {label}
      <span
        className={cn(
          "flex shrink-0 items-center justify-center",
          compact ? "size-[12px]" : "size-[16px]",
        )}
      >
        {icon ? (
          <Image
            src={icon}
            alt={iconAlt || ""}
            width={16}
            height={16}
            className={cn("object-contain", compact ? "size-[12px]" : "size-[16px]")}
          />
        ) : (
          <SendIcon color={textColor} className={compact ? "size-[12px]" : "size-[16px]"} />
        )}
      </span>
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        onClick={onClick}
        data-cta-name="apply_now"
        target={isExternal(href) ? "_blank" : undefined}
        rel={isExternal(href) ? "noopener noreferrer" : undefined}
        className={className}
        style={style}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      data-cta-name="apply_now"
      className={cn(className, "cursor-pointer")}
      style={style}
    >
      {content}
    </button>
  );
};

/* ------------------------------------------------------------------ */
/* NavBar4 — Atlas "Nav menu" (black bar)                              */
/* ------------------------------------------------------------------ */

const NAV_SHADOW =
  "0px 2px 6px 0px rgba(9,30,66,0.04), 0px 8px 12px 0px rgba(9,30,66,0.08)";

const NavBar2000 = ({ data }: { data: any }) => {
  const sectionData = data?.data ?? data;
  const isPreview = Boolean(data?.isPreview ?? false);
  const pathname = usePathname();

  const dispatch = useDispatch();
  const dropdownRef = useRef<HTMLElement>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDesktopDropdown, setOpenDesktopDropdown] = useState<number | null>(null);
  const [openMobileAccordion, setOpenMobileAccordion] = useState<number | null>(null);

  const {
    logo_icon = "",
    logo_icon_alt = "Logo",
    background_color = "#000000",
    nav_text_color = "#FFFFFF",
    active_menu_color = "#FFFFFF",
    border_color = "rgba(255,255,255,0.2)",
    divider_color = "",
    mobile_menu_text_color = "#FFFFFF",
    mobile_active_menu_color = "#FFFFFF",
    dropdown_bg_color = "#222222",
    dropdown_text_color = "#FFFFFF",
    // CTA-1 (phone)
    cta1_icon = "",
    cta1_icon_alt = "Call",
    cta1_label = "",
    cta1_href = "",
    cta1_bg_color = "rgba(0,0,0,0.1)",
    cta1_text_color = "#FFFFFF",
    cta1_border_color = "#FFFFFF",
    // CTA-2 (WhatsApp)
    cta2_icon = "",
    cta2_icon_alt = "WhatsApp",
    cta2_href = "",
    cta2_bg_color = "#4CAF51",
    cta2_text_color = "#FFFFFF",
    // CTA-3 (Apply Now) — desktop only, see the render block below.
    cta3_label = "APPLY NOW",
    cta3_href = "",
    cta3_bg_color = "#D61229",
    cta3_text_color = "#FFFFFF",
    cta3_icon = "",
    cta3_icon_alt = "Apply Now",
    // Mobile chrome
    hamburger_icon = "",
    hamburger_icon_alt = "Menu",
    mobile_cancel_icon = "",
    mobile_cancel_icon_alt = "Close",
    menuItems = [],
  } = sectionData ?? {};

  const showDropdownIcons = toBool(sectionData?.show_dropdown_icons, true);
  const cta1Enabled = toBool(sectionData?.cta1_enabled, true);
  const cta2Enabled = toBool(sectionData?.cta2_enabled, true);
  // Defaults off: existing navbars keep rendering exactly as they do today
  // until an editor turns the Apply Now button on.
  /* Two independent toggles. `cta3_enabled` is the desktop half (and remains
     the only flag NavBar 1047 reads); `cta3_enabled_mobile` is the mobile
     half, added by migration 20260818130000 and backfilled from
     `cta3_enabled`, so existing navbars render unchanged. */
  const cta3Enabled = toBool(sectionData?.cta3_enabled, false);
  const cta3EnabledMobile = toBool(sectionData?.cta3_enabled_mobile, false);
  /* Hamburger visibility. Defaults TRUE — a missing or NULL value must keep the
     hamburger, so only an explicit editor opt-out ever hides it. */
  const showHamburger = toBool(sectionData?.show_hamburger, true);
  /* Logo click. `logo_click_disabled` is inverted relative to NavBar3's
     `logo_redirect_enabled` so the CMS label reads as a plain instruction:
     TRUE means the logo does NOT link. Defaulting to false keeps the link for
     rows that predate the column. */
  const logoRedirects = !toBool(sectionData?.logo_click_disabled, false);

  // Transform backend menu items → routes (active + sorted), memoized.
  const menuRoutes: RouteItem[] = useMemo(() => {
    if (!Array.isArray(menuItems) || menuItems.length === 0) return [];
    return menuItems
      .filter((item: MenuItem) => item.status)
      .sort((a: MenuItem, b: MenuItem) => a.priority - b.priority)
      .slice(0, MAX_NAV_ITEMS)
      .map((item: MenuItem) => {
        const route: RouteItem = {
          id: item.id,
          route: item.url && item.url.trim() !== "" ? item.url : `/${item.slug}`,
          label: item.title,
          textColor: item.text_color,
        };
        if (item.children && item.children.length > 0) {
          route.nestedRoutes = item.children
            .filter((c: MenuItem) => c.status)
            .sort((a: MenuItem, b: MenuItem) => a.priority - b.priority)
            .map((c: MenuItem) => ({
              id: c.id,
              label: c.title,
              route: c.url && c.url.trim() !== "" ? c.url : `/${c.slug}`,
              textColor: c.text_color,
              icon: c.icon,
              iconAlt: c.icon_alt,
            }));
        }
        return route;
      });
  }, [menuItems]);

  const activePath = menuRoutes.find((r) => r.route === pathname)?.route;

  // Close desktop dropdown on outside click.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenDesktopDropdown(null);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const telHref = buildTelHref(cta1_href || cta1_label);
  const waHref = buildWhatsAppHref(cta2_href);
  const applyHref = String(cta3_href || "").trim();

  /* PRD: the divider is automatic — no manual toggle. It renders only when at
     least one of the phone / WhatsApp CTAs is actually set; Apply Now
     deliberately does not count toward it. This only gates the divider, so the
     CTAs' own render conditions are unchanged. */
  const phoneSet = cta1Enabled && Boolean(telHref || cta1_label);
  const whatsAppSet = cta2Enabled && Boolean(waHref);
  const showDivider = phoneSet || whatsAppSet;

  /* -- GTM: same events/payload shape as NavBar 3, fired from both the
        desktop and the mobile bar (this navbar renders each CTA twice) --- */
  const trackPhoneClick = () =>
    pushGtmEvent("ph_click", {
      section: "navbar",
      label: cta1_label || "phone",
      link_url: telHref || "",
    });

  /* Apply Now: navigate when a link is configured, otherwise open the
     registration lead drawer — the same fallback Faculty 2 / Program Fee 3 use. */
  const handleApplyClick = () => {
    pushGtmEvent("cta_click", {
      section: "navbar",
      label: cta3_label || "apply_now",
      link_url: applyHref || "",
    });
    if (applyHref) return;
    dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
  };

  const trackWhatsAppClick = () =>
    pushGtmEvent("wa_click", {
      section: "navbar",
      // No WhatsApp label field on this variant (icon-only CTA) — keep the same
      // literal NavBar 3 falls back to so both navbars report one value.
      label: "whatsapp",
      link_url: waHref || "",
    });

  /* -- reusable icon glyphs (uploaded image or SVG fallback) ---------- */
  const phoneGlyph = (sizeClass: string) =>
    cta1_icon ? (
      <Image src={cta1_icon} alt={cta1_icon_alt} width={24} height={24} className={cn("object-contain", sizeClass)} />
    ) : (
      <PhoneIcon color={cta1_text_color} className={sizeClass} />
    );

  const whatsAppGlyph = (sizeClass: string) =>
    cta2_icon ? (
      <Image src={cta2_icon} alt={cta2_icon_alt} width={24} height={24} className={cn("object-contain", sizeClass)} />
    ) : (
      <WhatsAppIcon color={cta2_text_color} className={sizeClass} />
    );

  const logoEl = (heightClass: string) => {
    if (!logo_icon) return null;
    const img = (
      <Image src={logo_icon} alt={logo_icon_alt} width={95} height={49} priority className={cn("w-auto object-contain object-left", heightClass)} />
    );
    /* The wrapper keeps identical classes in both branches so turning the link
       off changes only clickability, never layout. */
    return logoRedirects ? (
      <Link href="/" className={cn("block w-auto shrink-0", heightClass)} aria-label={logo_icon_alt}>
        {img}
      </Link>
    ) : (
      <span className={cn("block w-auto shrink-0", heightClass)}>{img}</span>
    );
  };

  return (
    <header className={cn("w-full", isPreview ? "relative" : "sticky top-0 z-50")}>
      {/* ---------------- Desktop bar (sm ≥ 1080) ---------------- */}
      <div
        className="hidden w-full items-center gap-[24px] px-[40px] py-[10px] sm:flex md:gap-[30px] md:px-[50px] lg:px-[60px] xl:px-[120px]"
        style={{ backgroundColor: background_color, borderBottom: `0.6px solid ${border_color}` }}
      >
        {logoEl("h-[32px] md:h-[40px] lg:h-[50px]")}

        {/* Menu links */}
        <nav
          ref={dropdownRef}
          className="flex min-w-0 flex-1 items-center justify-end gap-[33px] md:gap-[40px] lg:gap-[50px]"
        >
          {menuRoutes.map((route) =>
            route.nestedRoutes && route.nestedRoutes.length > 0 ? (
              <div key={route.id} className="relative">
                <button
                  type="button"
                  onClick={() =>
                    setOpenDesktopDropdown(openDesktopDropdown === route.id ? null : route.id)
                  }
                  className="flex items-center gap-1.5 whitespace-nowrap font-google-sans text-[11px] font-semibold leading-normal tracking-[0.11px] transition-opacity hover:opacity-80 md:text-[12px] md:tracking-[0.12px] lg:text-[14px] lg:tracking-[0.14px] xl:text-[16px] xl:tracking-[0.16px]"
                  style={{
                    color:
                      openDesktopDropdown === route.id || activePath === route.route
                        ? active_menu_color
                        : route.textColor || nav_text_color,
                  }}
                  aria-expanded={openDesktopDropdown === route.id}
                >
                  {route.label}
                  <ChevronIcon open={openDesktopDropdown === route.id} />
                </button>
                {openDesktopDropdown === route.id ? (
                  <div
                    className="absolute right-0 top-full z-50 mt-2.5 flex w-max min-w-[220px] max-w-[360px] flex-col gap-1 rounded-[16px] px-5 py-3.5 drop-shadow-[0px_4px_15px_rgba(0,0,0,0.25)]"
                    style={{ backgroundColor: dropdown_bg_color }}
                  >
                    {route.nestedRoutes.map((nested) => (
                      <Link
                        key={nested.id}
                        href={nested.route}
                        onClick={() => setOpenDesktopDropdown(null)}
                        className="flex w-full items-center gap-2.5 py-1 font-google-sans text-[11px] font-semibold leading-normal tracking-[0.11px] transition-opacity hover:opacity-80 md:text-[12px] md:tracking-[0.12px] lg:text-[14px] lg:tracking-[0.14px] xl:text-[16px] xl:tracking-[0.16px]"
                        style={{ color: nested.textColor || dropdown_text_color }}
                      >
                        {showDropdownIcons ? (
                          nested.icon ? (
                            <Image src={nested.icon} alt={nested.iconAlt || ""} width={20} height={20} className="shrink-0 object-contain" />
                          ) : (
                            <DropdownItemIcon color={dropdown_text_color} size={20} />
                          )
                        ) : null}
                        {nested.label}
                      </Link>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : (
              <Link
                key={route.id}
                href={route.route}
                className="whitespace-nowrap font-google-sans text-[11px] font-semibold leading-normal tracking-[0.11px] transition-opacity hover:opacity-80 md:text-[12px] md:tracking-[0.12px] lg:text-[14px] lg:tracking-[0.14px] xl:text-[16px] xl:tracking-[0.16px]"
                style={{
                  color:
                    activePath === route.route
                      ? active_menu_color
                      : route.textColor || nav_text_color,
                }}
              >
                {route.label}
              </Link>
            ),
          )}
        </nav>

        {/* Divider — automatic, only when a phone / WhatsApp CTA is set. */}
        {showDivider ? (
          <div
            className="h-[40px] w-px shrink-0"
            /* Falls back to `border_color`, which painted this before
               `divider_color` existed — so rows that never set it look
               unchanged. */
            style={{ backgroundColor: divider_color || border_color }}
          />
        ) : null}

        {/* Phone + WhatsApp + Apply Now */}
        <div className="flex shrink-0 items-center gap-[10px]">
          {cta1Enabled ? (
            <a
              href={telHref}
              onClick={trackPhoneClick}
              className="flex h-[30px] shrink-0 items-center gap-[6px] rounded-[26px] transition-opacity hover:opacity-90"
              style={{ backgroundColor: cta1_bg_color }}
              aria-label={cta1_label || cta1_icon_alt}
            >
              <span className="flex size-[14px] shrink-0 items-center justify-center md:size-[16px] xl:size-[18px]">
                {phoneGlyph("size-full")}
              </span>
              {cta1_label ? (
                <span
                  className="whitespace-nowrap font-google-sans text-[12px] font-semibold leading-none md:text-[14px] xl:text-[16px]"
                  style={{ color: cta1_text_color }}
                >
                  {cta1_label}
                </span>
              ) : null}
            </a>
          ) : null}

          {cta2Enabled ? (
            <a
              href={waHref}
              onClick={trackWhatsAppClick}
              target={waHref && isExternal(waHref) ? "_blank" : undefined}
              rel="noopener noreferrer"
              className="flex size-[36px] shrink-0 items-center justify-center rounded-[11px] transition-opacity hover:opacity-90 lg:size-[40px] xl:size-[44px]"
              style={{ backgroundColor: cta2_bg_color, boxShadow: NAV_SHADOW }}
              aria-label={cta2_icon_alt}
            >
              {whatsAppGlyph("size-[24px] xl:size-[26px]")}
            </a>
          ) : null}

          {/* Apply Now — pill CTA after WhatsApp. Governed by `cta3_enabled`,
              which is now the DESKTOP toggle only; the mobile bar below has its
              own, so an editor can show it on one breakpoint and not the other. */}
          {cta3Enabled && cta3_label ? (
            <ApplyNowCta
              label={cta3_label}
              href={applyHref}
              bgColor={cta3_bg_color}
              textColor={cta3_text_color}
              icon={cta3_icon}
              iconAlt={cta3_icon_alt}
              onClick={handleApplyClick}
            />
          ) : null}
        </div>
      </div>

      {/* ---------------- Mobile bar (< sm) ---------------- */}
      <div
        className="flex w-full items-center justify-between px-[24px] py-[10px] sm:hidden"
        style={{ backgroundColor: background_color, borderBottom: `0.6px solid ${border_color}` }}
      >
        {/* Design has no divider beside the mobile logo (Figma 12366:17449). */}
        <div className="flex items-center gap-[8px]">{logoEl("h-[32px]")}</div>

        <div className="flex items-center gap-[12px]">
          {cta1Enabled ? (
            <a
              href={telHref}
              onClick={trackPhoneClick}
              className="flex size-[36px] shrink-0 items-center justify-center rounded-full border transition-opacity hover:opacity-90"
              style={{ borderColor: cta1_border_color }}
              aria-label={cta1_label || cta1_icon_alt}
            >
              <span className="flex size-[18px] shrink-0 items-center justify-center">
                {phoneGlyph("size-full")}
              </span>
            </a>
          ) : null}

          {cta2Enabled ? (
            <a
              href={waHref}
              onClick={trackWhatsAppClick}
              target={waHref && isExternal(waHref) ? "_blank" : undefined}
              rel="noopener noreferrer"
              className="flex size-[36px] shrink-0 items-center justify-center rounded-full transition-opacity hover:opacity-90"
              style={{ backgroundColor: cta2_bg_color, boxShadow: NAV_SHADOW }}
              aria-label={cta2_icon_alt}
            >
              {whatsAppGlyph("size-[18px]")}
            </a>
          ) : null}

          {/* Apply Now — mobile. Sits between WhatsApp and the hamburger per
              Figma 13539:17221. Gated by its own `cta3_enabled_mobile` toggle,
              independent of the desktop bar. Label, link and colours stay the
              shared cta3_* fields; only the sizing and visibility differ. */}
          {cta3EnabledMobile && cta3_label ? (
            <ApplyNowCta
              compact
              label={cta3_label}
              href={applyHref}
              bgColor={cta3_bg_color}
              textColor={cta3_text_color}
              icon={cta3_icon}
              iconAlt={cta3_icon_alt}
              onClick={handleApplyClick}
            />
          ) : null}

          {/* Hamburger — optional. Hiding it also makes the drawer unreachable,
              which is the point: it is the only control that opens it. */}
          {showHamburger ? (
          <button
            type="button"
            className="flex size-[22px] shrink-0 items-center justify-center"
            onClick={() => setIsMobileMenuOpen((v) => !v)}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? (
              mobile_cancel_icon ? (
                <Image src={mobile_cancel_icon} alt={mobile_cancel_icon_alt} width={22} height={22} className="object-contain" />
              ) : (
                <CloseIcon color={nav_text_color} className="size-[22px]" />
              )
            ) : hamburger_icon ? (
              <Image src={hamburger_icon} alt={hamburger_icon_alt} width={22} height={22} className="object-contain" />
            ) : (
              <HamburgerIcon color={nav_text_color} className="size-[22px]" />
            )}
          </button>
          ) : null}
        </div>
      </div>

      {/* ---------------- Mobile drawer ---------------- */}
      {/* `showHamburger` is re-checked so the invariant holds no matter how the
          open state got set: no hamburger means no drawer. */}
      {showHamburger && isMobileMenuOpen ? (
        <div
          className="absolute left-0 right-0 top-full z-50 flex max-h-[80vh] flex-col gap-2 overflow-y-auto px-6 py-4 shadow-lg sm:hidden"
          style={{ backgroundColor: background_color, borderBottom: `0.6px solid ${border_color}` }}
        >
          {menuRoutes.map((route) =>
            route.nestedRoutes && route.nestedRoutes.length > 0 ? (
              <div key={route.id} className="w-full">
                <button
                  type="button"
                  onClick={() =>
                    setOpenMobileAccordion(openMobileAccordion === route.id ? null : route.id)
                  }
                  className={cn(
                    "flex w-full items-center justify-between py-1 font-google-sans text-[15px] tracking-[0.15px] leading-snug transition-opacity hover:opacity-80",
                    activePath === route.route ? "font-semibold" : "font-medium",
                  )}
                  style={{
                    color:
                      activePath === route.route ? mobile_active_menu_color : mobile_menu_text_color,
                  }}
                >
                  {route.label}
                  <ChevronIcon open={openMobileAccordion === route.id} />
                </button>
                {openMobileAccordion === route.id ? (
                  <div className="flex flex-col gap-1 py-1 pl-2.5">
                    {route.nestedRoutes.map((nested) => (
                      <Link
                        key={nested.id}
                        href={nested.route}
                        onClick={() => {
                          setOpenMobileAccordion(null);
                          setIsMobileMenuOpen(false);
                        }}
                        className="flex items-center gap-1.5 py-0.5 text-[13px] font-normal transition-opacity hover:opacity-80"
                        style={{ color: nested.textColor || mobile_menu_text_color }}
                      >
                        {showDropdownIcons ? (
                          nested.icon ? (
                            <Image src={nested.icon} alt={nested.iconAlt || ""} width={16} height={16} className="shrink-0 object-contain" />
                          ) : (
                            <DropdownItemIcon color={mobile_menu_text_color} size={16} />
                          )
                        ) : null}
                        {nested.label}
                      </Link>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : (
              <Link
                key={route.id}
                href={route.route}
                onClick={() => setIsMobileMenuOpen(false)}
                className={cn(
                  "py-1 font-google-sans text-[15px] tracking-[0.15px] leading-snug transition-opacity hover:opacity-80",
                  activePath === route.route ? "font-semibold" : "font-medium",
                )}
                style={{
                  color:
                    activePath === route.route ? mobile_active_menu_color : mobile_menu_text_color,
                }}
              >
                {route.label}
              </Link>
            ),
          )}
        </div>
      ) : null}
    </header>
  );
};

export default NavBar2000;
